/**
 * Google Maps helpers ported from the legacy OrderForm / GoogleMapsInput:
 * Places autocomplete on plain inputs, geocoding fallback, distance + polyline,
 * country detection. All no-ops when the Maps JS API is not loaded.
 */
export const gmaps = () => (window.google && window.google.maps ? window.google.maps : null);

const cityOfComponents = (components = []) => {
  const c =
    components.find((x) => x.types.includes("locality")) ||
    components.find((x) => x.types.includes("administrative_area_level_2")) ||
    components.find((x) => x.types.includes("administrative_area_level_1"));
  return c ? c.long_name : null;
};

const countryOf = (components = []) => {
  const c = components.find((x) => x.types.includes("country"));
  return c ? { country: c.long_name, country_code: c.short_name } : {};
};

/** Attach Places autocomplete to an <input>; onPick({address, lat, lng, country, country_code}). */
export const attachAutocomplete = (input, onPick, options = {}) => {
  const m = gmaps();
  if (!m || !m.places || !input) return () => {};
  const ac = new m.places.Autocomplete(input, {
    fields: ["formatted_address", "geometry", "address_components", "name"],
    ...options, // e.g. { types: ["(cities)"] } to suggest cities only
  });
  const l = ac.addListener("place_changed", () => {
    const p = ac.getPlace();
    if (!p || !p.geometry) return;
    onPick({
      address: p.formatted_address || p.name || input.value,
      city: cityOfComponents(p.address_components) || p.name || null,
      lat: p.geometry.location.lat(),
      lng: p.geometry.location.lng(),
      ...countryOf(p.address_components),
    });
  });
  return () => m.event.removeListener(l);
};

/** Geocode a typed address (fallback when the user did not pick a suggestion). */
export const geocode = (address) =>
  new Promise((resolve) => {
    const m = gmaps();
    if (!m || !address) return resolve(null);
    new m.Geocoder().geocode({ address }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const r = results[0];
        resolve({
          address: r.formatted_address || address,
          lat: r.geometry.location.lat(),
          lng: r.geometry.location.lng(),
          ...countryOf(r.address_components),
        });
      } else resolve(null);
    });
  });

const levelOf = (lat, lng) =>
  new Promise((resolve) => {
    const m = gmaps();
    new m.Geocoder().geocode({ location: new m.LatLng(lat, lng) }, (results, status) => {
      if (status === "OK" && results && results.length) {
        const types = results[0].types || [];
        const isCountry =
          types.includes("country") &&
          !types.includes("locality") &&
          !types.includes("administrative_area_level_2") &&
          !types.includes("sublocality");
        resolve({ isCity: !isCountry, ...countryOf(results[0].address_components) });
      } else resolve({ isCity: true });
    });
  });

/** {distance, polyline, from_country...} — same logic as legacy OrderForm.getDistance. */
export const routeInfo = async (from, to) => {
  const m = gmaps();
  const out = { distance: null, route_polyline: null };
  if (!m || !from?.lat || !to?.lat) return out;
  let a = {};
  let b = {};
  try {
    [a, b] = await Promise.all([levelOf(from.lat, from.lng), levelOf(to.lat, to.lng)]);
  } catch (e) {
    /* continue without country-level info */
  }
  if (a.country) {
    out.from_country = a.country;
    out.from_country_code = a.country_code;
  }
  if (b.country) {
    out.to_country = b.country;
    out.to_country_code = b.country_code;
  }
  if (a.isCity === false || b.isCity === false) return out;
  await new Promise((resolve) => {
    try {
      new m.DirectionsService().route(
        {
          origin: new m.LatLng(from.lat, from.lng),
          destination: new m.LatLng(to.lat, to.lng),
          travelMode: m.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === m.DirectionsStatus.OK) {
            out.distance = result.routes[0].legs[0].distance.text;
            out.route_polyline = result.routes[0].overview_polyline;
          }
          resolve();
        },
      );
    } catch (e) {
      resolve();
    }
  });
  return out;
};
